import { useEffect, useMemo, useState } from 'react';
import { EstimatedTag } from './ui/EstimatedTag';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { KnowledgeGraph } from '@lizrd/contracts';
import { Permission, roleHasPermissions } from '@lizrd/contracts';
import { useAuthStore } from '../store/auth';
import {
  explainResource,
  getAiStatus,
  getRecommendations,
  getResourceExplanation,
} from '../lib/api';
import { Card } from './ui/card';
import { StepProgress } from './ui/step-progress';
import { type StepState } from './ui/stepper';
import { MarkdownLite } from './ui/markdown-lite';
import {
  consoleUrl,
  costOf,
  nearestRepo,
  PROVIDER_ACCENT,
  typeLabel,
  usd,
} from '../lib/graph-visuals';
import { NodeTypeIcon } from './node-icons';
import { Lightbulb, Sparkles } from 'lucide-react';

type GraphNode = KnowledgeGraph['nodes'][number];
const PAGE_SIZE = 10;
// A resource explanation may only be regenerated once every 15 minutes.
const REGEN_COOLDOWN_MS = 15 * 60_000;

type Metrics = {
  cpuPct: number | null;
  memPct: number | null;
  throughput: { value: number; unit: string } | null;
};
const metricsOf = (n: GraphNode): Metrics | null => {
  const m = (n.properties as Record<string, unknown>).metrics;
  return m && typeof m === 'object' ? (m as Metrics) : null;
};

const compact = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 });

/** A metric tile: big value, a caption, and an optional coloured status word. */
function MetricBox({
  label,
  value,
  unit,
  status,
  tone = 'text-bone',
}: {
  label: string;
  value: string;
  unit?: string;
  status?: string;
  tone?: string;
}) {
  return (
    <div className="min-w-[104px] flex-1 rounded-md border border-hairline bg-surface-2/40 p-3 text-center">
      <div className="text-[0.6rem] font-semibold uppercase tracking-wide text-muted">{label}</div>
      <div className={`font-display text-2xl font-bold leading-tight ${tone}`}>
        {value}
        {unit && <span className="ml-0.5 text-xs font-normal text-muted">{unit}</span>}
      </div>
      <div className="text-[0.6rem] text-muted">{status ?? '7-day avg'}</div>
    </div>
  );
}

/** Utilisation band → colour + word. Extremes flag cost waste (idle) or risk (hot). */
function utilTone(pct: number): { tone: string; status: string } {
  if (pct < 20) return { tone: 'text-info', status: 'Underused' };
  if (pct > 85) return { tone: 'text-warning', status: 'Hot' };
  return { tone: 'text-bone', status: 'Healthy' };
}

/**
 * The metrics that help explain a resource's cost: CPU / memory utilisation
 * (idle capacity is wasted spend) and throughput (the work it does — so cost can
 * be read per unit of work). Only the metrics we actually have for a resource
 * render, and a low-utilisation hint calls out likely over-provisioning.
 */
function MetricBoxes({ metrics: m }: { metrics: Metrics | null }) {
  const hasAny = !!m && (m.cpuPct != null || m.memPct != null || m.throughput != null);
  if (!m || !hasAny) {
    return <p className="text-[0.65rem] text-muted">No metrics available.</p>;
  }
  const cpu = m.cpuPct != null ? utilTone(m.cpuPct) : null;
  const mem = m.memPct != null ? utilTone(m.memPct) : null;
  const idle = Math.max(m.cpuPct ?? 101, m.memPct ?? 101) < 20; // both/known low
  const anyUtil = m.cpuPct != null || m.memPct != null;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {m.cpuPct != null && (
          <MetricBox
            label="CPU"
            value={`${Math.round(m.cpuPct)}%`}
            tone={cpu!.tone}
            status={cpu!.status}
          />
        )}
        {m.memPct != null && (
          <MetricBox
            label="Memory"
            value={`${Math.round(m.memPct)}%`}
            tone={mem!.tone}
            status={mem!.status}
          />
        )}
        {m.throughput && (
          <MetricBox
            label="Throughput"
            value={compact.format(m.throughput.value)}
            unit={m.throughput.unit}
            status="recent"
          />
        )}
      </div>
      {anyUtil && idle && (
        <p className="text-[0.65rem] text-warning">
          ⚠ Low utilisation — this resource is likely over-provisioned, so part of its cost is idle
          capacity.
        </p>
      )}
    </div>
  );
}

/**
 * Your Cost Drivers Explained (Resource Intelligence, Phase 5): the heaviest
 * resources ranked by $/mo (paginated), each expanding into a dossier — who owns
 * it, throughput, CPU/memory utilisation. An on-demand "Explain deeper" button
 * generates a director-friendly AI narrative, cached encrypted server-side so
 * repeat views never re-bill the model.
 */
export function CostDrivers({
  graph,
  envId,
  focus,
}: {
  graph: KnowledgeGraph;
  envId: string;
  focus?: { id: string; nonce: number } | null;
}) {
  const owner = nearestRepo(graph);
  const repos = graph.nodes.filter((n) => n.type === 'repository');
  const repoName = new Map(repos.map((n) => [n.id, n.name]));
  const drivers = graph.nodes
    .filter((n) => costOf(n.properties) > 0)
    .sort((a, b) => costOf(b.properties) - costOf(a.properties));

  // Local pagination — flipping pages re-renders only this box, not the page.
  const [page, setPage] = useState(0);
  const pageCount = Math.ceil(drivers.length / PAGE_SIZE);
  const current = Math.min(page, Math.max(0, pageCount - 1));
  const shown = drivers.slice(current * PAGE_SIZE, current * PAGE_SIZE + PAGE_SIZE);

  // "See why" from the guide: jump to the driver's page, expand its row, scroll to it.
  useEffect(() => {
    if (!focus) return;
    const index = drivers.findIndex((n) => n.id === focus.id);
    if (index < 0) return;
    setPage(Math.floor(index / PAGE_SIZE));
    const raf = requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        const el = document.getElementById(`driver-${focus.id}`) as HTMLDetailsElement | null;
        if (el) {
          el.open = true;
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }),
    );
    return () => cancelAnimationFrame(raf);
  }, [focus]);

  // Whether the server has an AI provider configured; the button is only live if so.
  const aiStatus = useQuery({
    queryKey: ['ai-status', envId],
    queryFn: () => getAiStatus(envId),
    enabled: !!envId && drivers.length > 0,
    staleTime: 5 * 60_000,
  });
  const aiEnabled = aiStatus.data?.enabled ?? false;

  // Open optimization action items, counted per resource, so a cost driver can
  // surface how many recommendations target it (Phase 7 link-through).
  const recommendations = useQuery({
    queryKey: ['recommendations', envId],
    queryFn: () => getRecommendations(envId),
    enabled: !!envId && drivers.length > 0,
    refetchInterval: 60_000,
  });
  const optimizationsByNode = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of recommendations.data?.items ?? [])
      if (r.status === 'open') m.set(r.nodeExternalId, (m.get(r.nodeExternalId) ?? 0) + 1);
    return m;
  }, [recommendations.data]);

  if (!drivers.length) return null;

  return (
    <Card className="space-y-3 p-5 text-sm">
      <div>
        <h2 className="font-display text-lg font-bold text-bone">Your Cost Drivers Explained</h2>
        <p className="text-xs text-muted">
          Your heaviest resources — who owns them, how hard they work, and why they cost what they
          do.
        </p>
      </div>
      <div className="space-y-1.5">
        {shown.map((n) => {
          const ownerId = owner.get(n.id);
          return (
            <DriverRow
              key={n.id}
              node={n}
              envId={envId}
              aiEnabled={aiEnabled}
              ownRepo={ownerId ? repoName.get(ownerId) : undefined}
              optimizationCount={optimizationsByNode.get(n.externalId) ?? 0}
            />
          );
        })}
      </div>

      {pageCount > 1 && (
        <div className="flex items-center justify-between border-t border-hairline pt-3 text-xs">
          <span className="text-muted">
            Showing {current * PAGE_SIZE + 1}–{current * PAGE_SIZE + shown.length} of{' '}
            {drivers.length}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(current - 1)}
              disabled={current === 0}
              className="rounded-sm border border-border px-2.5 py-1 font-semibold text-bone transition-colors hover:bg-surface-2 disabled:opacity-40"
            >
              ← Prev
            </button>
            <span className="text-muted">
              Page {current + 1} / {pageCount}
            </span>
            <button
              onClick={() => setPage(current + 1)}
              disabled={current >= pageCount - 1}
              className="rounded-sm border border-border px-2.5 py-1 font-semibold text-bone transition-colors hover:bg-surface-2 disabled:opacity-40"
            >
              Next →
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}

function DriverRow({
  node: n,
  envId,
  aiEnabled,
  ownRepo,
  optimizationCount,
}: {
  node: GraphNode;
  envId: string;
  aiEnabled: boolean;
  ownRepo?: string;
  optimizationCount: number;
}) {
  const [open, setOpen] = useState(false);
  const c = costOf(n.properties);
  const m = metricsOf(n);
  const url = consoleUrl(n);
  const accent = PROVIDER_ACCENT[n.provider] ?? '#8b93a7';

  return (
    <details
      id={`driver-${n.id}`}
      className="group rounded-md border border-hairline bg-surface-2/40"
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
    >
      <summary className="flex cursor-pointer list-none items-center gap-2 p-3">
        <span className="inline-block w-3 shrink-0 text-muted transition-transform group-open:rotate-90">
          ▸
        </span>
        <NodeTypeIcon type={n.type} className="h-4 w-4 text-muted" />
        <span className="truncate font-semibold text-bone" title={n.name}>
          {n.name}
        </span>
        <span className="shrink-0 text-[0.65rem] text-muted">{typeLabel(n.type, n.provider)}</span>
        <span
          className="shrink-0 rounded-full px-1.5 py-0.5 text-[0.55rem] font-bold"
          style={{ backgroundColor: `${accent}22`, color: accent }}
        >
          {n.provider}
        </span>
        {optimizationCount > 0 && (
          <span
            className="shrink-0 rounded-full border border-iris/40 bg-iris/10 px-1.5 py-0.5 text-[0.55rem] font-bold text-iris"
            title={`${optimizationCount} open optimization action item${optimizationCount === 1 ? '' : 's'}`}
          >
            <Lightbulb aria-hidden className="mr-1 inline-block h-3 w-3 align-text-bottom" />
            {optimizationCount}
          </span>
        )}
        <span className="ml-auto inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap">
          <EstimatedTag compact />
          <span>
            <strong className="font-display text-iris">{usd(c)}</strong>
            <span className="text-[0.65rem] text-muted">/mo</span>
          </span>
        </span>
      </summary>

      <div className="space-y-3 border-t border-hairline px-4 py-3">
        <div className="text-xs text-muted">
          Owned by <span className="text-bone">{ownRepo ?? 'Unattributed'}</span>
        </div>

        {optimizationCount > 0 && (
          <Link
            to={`../cost-optimization?resource=${encodeURIComponent(n.externalId)}`}
            className="flex items-center gap-2.5 rounded-md border border-iris/30 bg-iris/5 p-3 transition-colors hover:bg-iris/10"
          >
            <Lightbulb aria-hidden className="h-5 w-5 shrink-0 text-iris" />
            <span className="text-xs text-haze">
              <span className="font-semibold text-bone">
                {optimizationCount} open optimization action item
                {optimizationCount === 1 ? '' : 's'}
              </span>{' '}
              for this resource.
            </span>
            <span className="ml-auto shrink-0 text-xs font-semibold text-viridian">
              View optimizations →
            </span>
          </Link>
        )}

        <MetricBoxes metrics={m} />

        <ExplainDeeper node={n} envId={envId} aiEnabled={aiEnabled} open={open} />

        {url && (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="inline-block text-xs text-viridian hover:underline"
          >
            Open in console ↗
          </a>
        )}
      </div>
    </details>
  );
}

const PHASES = ['Gathering facts', 'Understanding capabilities', 'Writing explanation'] as const;
// While the request is in flight, advance only to the middle stage — holding an
// honest "in progress" state reads better than parking on the final step.
const HOLD_AT = PHASES.length - 2;

/**
 * On-demand AI narrative for one resource. Loads any cached explanation when the
 * row opens; generation is an explicit button click that walks a staged progress
 * bar (consistent with the domain-sync pipeline) while the model runs.
 */
function ExplainDeeper({
  node,
  envId,
  aiEnabled,
  open,
}: {
  node: GraphNode;
  envId: string;
  aiEnabled: boolean;
  open: boolean;
}) {
  const qc = useQueryClient();
  const [phase, setPhase] = useState(0);
  const [error, setError] = useState<string | null>(null);
  // Anyone who can act on cost intelligence may generate (everyone except a Viewer).
  const role = useAuthStore((s) => s.user?.role);
  const canGenerate = role ? roleHasPermissions(role, [Permission.RecommendationsManage]) : false;

  const cached = useQuery({
    queryKey: ['explanation', envId, node.id],
    queryFn: () => getResourceExplanation(envId, node.id),
    enabled: open && aiEnabled,
    staleTime: 60 * 60_000,
  });

  const generate = useMutation({
    mutationFn: () => explainResource(envId, node.id),
    onMutate: () => {
      setError(null);
      setPhase(0);
    },
    onSuccess: (result) => {
      qc.setQueryData(['explanation', envId, node.id], result);
    },
    onError: (e) => {
      setError((e as Error).message || 'Could not generate an explanation.');
    },
  });

  // Walk the staged progress while the request is in flight (the real work is a
  // single call, but the stages make the wait legible). We stop at HOLD_AT so it
  // never falsely reaches the last stage before the explanation actually returns.
  useEffect(() => {
    if (!generate.isPending) return;
    const timer = setInterval(() => setPhase((p) => Math.min(p + 1, HOLD_AT)), 900);
    return () => clearInterval(timer);
  }, [generate.isPending]);

  if (!aiEnabled) {
    return (
      <p className="text-[0.65rem] italic text-muted">
        AI explanations are not enabled on this server.
      </p>
    );
  }

  const explanation = cached.data;
  const steps: { label: string; state: StepState }[] = PHASES.map((label, i) => ({
    label,
    state: i < phase ? 'done' : i === phase ? 'active' : 'pending',
  }));
  // Regeneration is throttled to once every 15 minutes (mirrors the server).
  const cooldownRemainingMs = explanation
    ? REGEN_COOLDOWN_MS - (Date.now() - new Date(explanation.generatedAt).getTime())
    : 0;
  const inCooldown = cooldownRemainingMs > 0;
  const cooldownMins = Math.ceil(cooldownRemainingMs / 60_000);

  return (
    <div className="space-y-2">
      {generate.isPending ? (
        <div className="rounded-md border border-hairline bg-surface-2/60 p-3">
          <StepProgress steps={steps} />
        </div>
      ) : explanation ? (
        <div className="space-y-2 rounded-md border border-iris/30 bg-iris/5 p-3">
          <MarkdownLite text={explanation.content} />
          <div className="flex items-center justify-between gap-2 border-t border-iris/20 pt-2">
            <span className="text-[0.65rem] text-muted">
              Last generated {new Date(explanation.generatedAt).toLocaleString()}
            </span>
            {canGenerate && (
              <button
                onClick={() => generate.mutate()}
                disabled={inCooldown}
                title={inCooldown ? `Regenerate available in ${cooldownMins} min` : undefined}
                className="rounded-sm border border-iris/50 bg-iris/10 px-2.5 py-1 text-xs font-semibold text-iris transition-colors hover:bg-iris/20 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {inCooldown ? (
                  `Regenerate in ${cooldownMins}m`
                ) : (
                  <>
                    <Sparkles
                      aria-hidden
                      className="mr-1 inline-block h-3.5 w-3.5 align-text-bottom"
                    />
                    Regenerate
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      ) : canGenerate ? (
        <button
          onClick={() => generate.mutate()}
          disabled={cached.isLoading}
          className="rounded-sm border border-iris/40 px-2.5 py-1 text-xs font-semibold text-iris transition-colors hover:bg-iris/10 disabled:opacity-50"
        >
          <Sparkles aria-hidden className="mr-1 inline-block h-3.5 w-3.5 align-text-bottom" />
          Explain deeper
        </button>
      ) : (
        <p className="text-[0.65rem] italic text-muted">No explanation generated yet.</p>
      )}
      {error && <p className="text-[0.65rem] text-danger">{error}</p>}
    </div>
  );
}
